import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Check,
  X,
  Loader2,
  AlertTriangle,
  ArrowRight,
  Building2,
  Mail,
  Phone,
  MapPin,
  Globe,
  Sparkles,
  CheckCircle2,
  XCircle,
  ExternalLink
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConfidenceBadge = ({ confidence }) => {
  if (confidence >= 0.8) return <Badge className="bg-red-600 text-[10px]">High match</Badge>;
  if (confidence >= 0.6) return <Badge className="bg-amber-500 text-[10px]">Likely match</Badge>;
  return <Badge className="bg-stone-400 text-[10px]">Possible match</Badge>;
};

const DuplicateReviewModal = ({ open, onOpenChange, reviewId, onResolved }) => {
  const [loading, setLoading] = useState(true);
  const [review, setReview] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const fetchReview = useCallback(async () => {
    if (!reviewId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pending-reviews/${reviewId}`, { withCredentials: true });
      setReview(res.data);
      // Default all to "skip"
      const defaults = {};
      res.data.leads.forEach((_, idx) => { defaults[idx] = 'skip'; });
      setDecisions(defaults);
    } catch (err) {
      console.error('Failed to load review:', err);
      toast.error('Failed to load duplicate review');
    } finally {
      setLoading(false);
    }
  }, [reviewId]);

  useEffect(() => {
    if (open && reviewId) fetchReview();
  }, [open, reviewId, fetchReview]);

  const setAction = (idx, action) => {
    setDecisions(prev => ({ ...prev, [idx]: action }));
  };

  const addAll = () => {
    const all = {};
    review.leads.forEach((_, idx) => { all[idx] = 'add'; });
    setDecisions(all);
  };

  const skipAll = () => {
    const all = {};
    review.leads.forEach((_, idx) => { all[idx] = 'skip'; });
    setDecisions(all);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        decisions: Object.entries(decisions).map(([idx, action]) => ({
          index: parseInt(idx),
          action
        }))
      };
      const res = await axios.post(`${API}/pending-reviews/${reviewId}/resolve`, payload, { withCredentials: true });
      const { added, skipped } = res.data;
      toast.success(`Review complete: ${added} added, ${skipped} skipped`);
      if (onResolved) onResolved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resolve review');
    } finally {
      setSubmitting(false);
    }
  };

  const addCount = Object.values(decisions).filter(d => d === 'add').length;
  const skipCount = Object.values(decisions).filter(d => d === 'skip').length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="duplicate-review-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Review Potential Duplicates
          </DialogTitle>
          <DialogDescription>
            These leads may already exist in your pipeline. Review each and decide whether to add or skip.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : review && review.leads?.length > 0 ? (
          <>
            {/* Quick actions */}
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-muted-foreground">
                {review.leads.length} potential duplicate{review.leads.length !== 1 ? 's' : ''}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={addAll} data-testid="add-all-btn">
                  <CheckCircle2 className="mr-1.5 h-3.5 w-3.5 text-green-600" />Add All
                </Button>
                <Button variant="outline" size="sm" onClick={skipAll} data-testid="skip-all-btn">
                  <XCircle className="mr-1.5 h-3.5 w-3.5 text-red-500" />Skip All
                </Button>
              </div>
            </div>

            <Separator />

            {/* Lead comparison cards */}
            <div className="space-y-4 py-2">
              {review.leads.map((entry, idx) => {
                const nl = entry.lead_data;
                const match = entry.match;
                const action = decisions[idx] || 'skip';
                // Stable key combines existing lead id + new business name. The
                // outer state map (decisions) is still index-keyed because the
                // server payload is immutable for the life of this modal.
                const stableKey = `${match.existing_lead_id}__${nl.business_name || idx}`;

                return (
                  <div
                    key={stableKey}
                    className={`rounded-lg border-2 transition-colors ${
                      action === 'add' ? 'border-green-300 bg-green-50/50' : action === 'skip' ? 'border-stone-200 bg-stone-50/50' : 'border-stone-200'
                    }`}
                    data-testid={`dup-card-${idx}`}
                  >
                    <div className="p-4">
                      {/* New lead vs existing */}
                      <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-start">
                        {/* New lead */}
                        <div>
                          <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium mb-1.5">New Lead</p>
                          <p className="font-semibold text-sm">{nl.business_name}</p>
                          {nl.email && <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><Mail className="h-3 w-3" />{nl.email}</p>}
                          {nl.phone && <p className="text-xs text-muted-foreground flex items-center gap-1"><Phone className="h-3 w-3" />{nl.phone}</p>}
                          {(nl.city || nl.state) && <p className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" />{[nl.city, nl.state].filter(Boolean).join(', ')}</p>}
                          {nl.website && <p className="text-xs text-muted-foreground flex items-center gap-1"><Globe className="h-3 w-3" />{nl.website.replace(/https?:\/\//, '').slice(0, 30)}</p>}
                          {nl.claude_score && (
                            <Badge variant="outline" className="mt-1.5 text-[10px]">
                              <Sparkles className="h-2.5 w-2.5 mr-0.5" />{nl.claude_score}
                            </Badge>
                          )}
                        </div>

                        {/* Arrow + confidence */}
                        <div className="flex flex-col items-center gap-1 pt-4">
                          <ConfidenceBadge confidence={match.confidence} />
                          <ArrowRight className="h-4 w-4 text-muted-foreground" />
                          <span className="text-[10px] text-muted-foreground">{Math.round(match.confidence * 100)}%</span>
                        </div>

                        {/* Existing lead */}
                        <div>
                          <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium mb-1.5">Existing Lead</p>
                          <p className="font-semibold text-sm">{match.existing_business_name}</p>
                          {match.existing_email && <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><Mail className="h-3 w-3" />{match.existing_email}</p>}
                          {match.existing_phone && <p className="text-xs text-muted-foreground flex items-center gap-1"><Phone className="h-3 w-3" />{match.existing_phone}</p>}
                          {match.existing_city && <p className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" />{match.existing_city}</p>}
                          <Badge variant="outline" className="mt-1.5 text-[10px] capitalize">{match.existing_stage?.replace('_', ' ')}</Badge>
                          <a href={`/leads/${match.existing_lead_id}`} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-600 flex items-center gap-0.5 mt-1 hover:underline">
                            <ExternalLink className="h-2.5 w-2.5" />View
                          </a>
                        </div>
                      </div>

                      {/* Match reasons */}
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {match.reasons.map((reason) => (
                          <span key={reason} className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">{reason}</span>
                        ))}
                      </div>

                      {/* Action buttons */}
                      <div className="flex gap-2 mt-3">
                        <Button
                          size="sm"
                          variant={action === 'add' ? 'default' : 'outline'}
                          className={action === 'add' ? 'bg-green-600 hover:bg-green-700 text-white' : ''}
                          onClick={() => setAction(idx, 'add')}
                          data-testid={`add-btn-${idx}`}
                        >
                          <Check className="mr-1 h-3.5 w-3.5" />Add Anyway
                        </Button>
                        <Button
                          size="sm"
                          variant={action === 'skip' ? 'default' : 'outline'}
                          className={action === 'skip' ? 'bg-stone-600 hover:bg-stone-700 text-white' : ''}
                          onClick={() => setAction(idx, 'skip')}
                          data-testid={`skip-btn-${idx}`}
                        >
                          <X className="mr-1 h-3.5 w-3.5" />Skip Duplicate
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <Separator />

            {/* Submit */}
            <div className="flex items-center justify-between py-2">
              <div className="text-sm text-muted-foreground">
                <span className="text-green-600 font-medium">{addCount} to add</span>
                <span className="mx-2">|</span>
                <span className="text-stone-500 font-medium">{skipCount} to skip</span>
              </div>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-forest hover:bg-forest-dark text-white"
                data-testid="confirm-review-btn"
              >
                {submitting ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</>
                ) : (
                  <>Confirm Decisions</>
                )}
              </Button>
            </div>
          </>
        ) : (
          <p className="text-sm text-muted-foreground py-8 text-center">No pending duplicates to review.</p>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DuplicateReviewModal;
